import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { emitCameraThresholdEvents } from 'src/services/cameraThresholdEvents';
import {
  APP_EVENTS,
  CAMERA_CONNECTION_METHOD,
  CAMERA_CONNECTION_OUTCOME,
  captureAppEvent,
  secondsSince,
} from 'src/services/posthogEvents';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { AutoConnectCameraStatus } from 'src/models/Camera.model';
import CameraConnection from 'src/models/Camera/CameraConnection';
import { I18NextNS } from 'src/models/Language.model';

import { captureError } from 'src/utils/errorReporting';

const useAutoConnectCamera = () => {
  const { cameraIP } = useLocalStore();
  const { setCameraData, setAutoConnectCameraStatus } = useRemoteBoundStore();

  const { t } = useTranslation(I18NextNS.COMMON);

  const autoConnectCamera = useCallback(async () => {
    if (!cameraIP) {
      setAutoConnectCameraStatus(AutoConnectCameraStatus.FAILED);
      return;
    }

    const connectStart = performance.now();

    try {
      const cameraData = await CameraConnection.loadCameraData(
        cameraIP,
        t('message.error.cameraIpMissingMsg'),
      );
      await CameraConnection.getInstance().say(
        'Camera successfully connected.',
      );
      setCameraData(cameraData);
      emitCameraThresholdEvents(cameraData);
      setAutoConnectCameraStatus(AutoConnectCameraStatus.CONNECTED);
      captureAppEvent(APP_EVENTS.CAMERA_CONNECTION_ATTEMPTED, {
        method: CAMERA_CONNECTION_METHOD.AUTO,
        outcome: CAMERA_CONNECTION_OUTCOME.SUCCESS,
        via_remembered: true,
        duration_seconds: secondsSince(connectStart),
        camera_serial_number: cameraData.deviceSN,
        camera_version: cameraData.versionInfo?.version,
        camera_side_gain: cameraData.sideGain.data,
        camera_main_gain: cameraData.mainGain.data,
      });
    } catch (error) {
      setAutoConnectCameraStatus(AutoConnectCameraStatus.FAILED);
      captureAppEvent(APP_EVENTS.CAMERA_CONNECTION_ATTEMPTED, {
        method: CAMERA_CONNECTION_METHOD.AUTO,
        outcome: CAMERA_CONNECTION_OUTCOME.FAILURE,
        via_remembered: true,
        duration_seconds: secondsSince(connectStart),
      });

      captureError(
        error,
        'Failed to connect to the camera using the existing IP address',
      );
    }
  }, [cameraIP, setAutoConnectCameraStatus, setCameraData, t]);

  return { autoConnectCamera };
};

export default useAutoConnectCamera;
